import { useState, useEffect, useCallback } from 'react';

const API = process.env.REACT_APP_API_URL || '';

const STATUS_CFG = {
  HEALTHY:  { color: '#27ae60', bg: '#eafaf1', border: '#27ae60', icon: '✅' },
  WARNING:  { color: '#e67e22', bg: '#fef5e7', border: '#f39c12', icon: '⚠️' },
  CRITICAL: { color: '#c0392b', bg: '#fdecea', border: '#e74c3c', icon: '🔴' },
  UNKNOWN:  { color: '#888',    bg: '#f9f9f9', border: '#ccc',    icon: '❓' },
};

const COMPONENT_ICONS = {
  Fixture_Clamp:       '🔧',
  Temperature_Sensor:  '🌡️',
  Robot_Joints:        '🤖',
};

export default function PredictiveMaintenance() {
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [lastRun,  setLastRun]  = useState(null);

  const runAnalysis = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res  = await fetch(`${API}/api/maintenance`);
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Analysis failed');
      setData(json);
      setLastRun(new Date());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    runAnalysis();
    const interval = setInterval(runAnalysis, 5 * 60 * 1000); // every 5 min
    return () => clearInterval(interval);
  }, [runAnalysis]);

  const components = data?.components || {};
  const overall    = data?.overallHealth ?? null;

  return (
    <div style={s.page}>
      {/* ── Header ── */}
      <div style={s.header}>
        <div>
          <h2 style={s.heading}>🔧 Predictive Maintenance AI</h2>
          <p style={s.sub}>
            Statistical RUL estimation, wear pattern detection and maintenance scheduling
            for Fixture Clamp, Temperature Sensor and Robot Joints
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {overall !== null && <HealthGauge score={overall} />}
          <button style={s.refreshBtn} onClick={runAnalysis} disabled={loading}>
            {loading ? '…' : '↻ Run Analysis'}
          </button>
        </div>
      </div>

      {error && <div style={s.errorBox}>⚠️ {error}</div>}
      {lastRun && <p style={s.lastRun}>Last analysis: {lastRun.toLocaleTimeString()} — auto-refresh every 5 min</p>}

      {/* ── Component cards ── */}
      {loading && !data ? (
        <p style={{ color: '#888', textAlign: 'center', padding: 40 }}>Running analysis…</p>
      ) : (
        <div style={s.cardGrid}>
          {Object.entries(components).map(([name, analysis]) => (
            <ComponentCard key={name} name={name} analysis={analysis} />
          ))}
        </div>
      )}

      {/* ── Maintenance timeline ── */}
      {data && <MaintenanceTimeline components={components} />}

      {/* ── How it works ── */}
      <HowItWorks />
    </div>
  );
}

function HealthGauge({ score }) {
  const color = score >= 80 ? '#27ae60' : score >= 60 ? '#e67e22' : '#c0392b';
  return (
    <div style={{ textAlign: 'center', background: '#fff', borderRadius: 8, padding: '10px 16px', border: '1px solid #ddd' }}>
      <div style={{ fontSize: 28, fontWeight: 700, color }}>
        {score}<span style={{ fontSize: 14, color: '#888' }}>%</span>
      </div>
      <div style={{ fontSize: 11, color: '#888' }}>Fleet Health</div>
    </div>
  );
}

function ComponentCard({ name, analysis }) {
  const cfg   = STATUS_CFG[analysis.status] || STATUS_CFG.UNKNOWN;
  const icon  = COMPONENT_ICONS[name] || '⚙️';
  const label = name.replace(/_/g, ' ');

  return (
    <div style={{ ...s.card, borderTop: `4px solid ${cfg.border}` }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 22 }}>{icon}</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14 }}>{label}</div>
            <div style={{ fontSize: 11, color: '#888' }}>{analysis.dataPoints || 0} data points (24h)</div>
          </div>
        </div>
        <span style={{ ...s.statusBadge, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}>
          {cfg.icon} {analysis.status}
        </span>
      </div>

      {/* Health score bar */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
          <span style={{ color: '#555' }}>Health Score</span>
          <span style={{ fontWeight: 700, color: cfg.color }}>{analysis.healthScore}%</span>
        </div>
        <div style={s.barBg}>
          <div style={{ ...s.barFill, width: `${analysis.healthScore}%`, background: cfg.border }} />
        </div>
      </div>

      {/* RUL */}
      {analysis.rulHours != null && (
        <div style={{ ...s.infoRow, background: cfg.bg }}>
          <span style={{ fontSize: 12, color: '#555' }}>Remaining Useful Life</span>
          <span style={{ fontWeight: 700, color: cfg.color, fontSize: 13 }}>
            {analysis.rulHours > 8760
              ? '>1 year'
              : analysis.rulHours > 720
              ? `~${Math.round(analysis.rulHours / 720)}mo`
              : analysis.rulHours > 24
              ? `~${Math.round(analysis.rulHours / 24)}d`
              : `~${analysis.rulHours}h`}
          </span>
        </div>
      )}

      {/* Next service */}
      {analysis.nextService && (
        <div style={s.infoRow}>
          <span style={{ fontSize: 12, color: '#555' }}>Recommended Service</span>
          <span style={{ fontWeight: 600, fontSize: 12, color: '#0073bb' }}>{analysis.nextService}</span>
        </div>
      )}

      {/* Findings */}
      {analysis.findings?.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#555', marginBottom: 4 }}>FINDINGS</div>
          {analysis.findings.map((f, i) => (
            <div key={i} style={{ fontSize: 12, color: '#444', padding: '4px 0', borderBottom: '1px solid #f0f0f0', display: 'flex', gap: 6 }}>
              <span style={{ color: cfg.color }}>•</span>{f}
            </div>
          ))}
        </div>
      )}

      {analysis.findings?.length === 0 && (
        <div style={{ fontSize: 12, color: '#27ae60', marginTop: 8 }}>No issues detected — operating normally</div>
      )}
    </div>
  );
}

function MaintenanceTimeline({ components }) {
  const scheduled = Object.entries(components)
    .filter(([, a]) => a.nextService)
    .sort(([, a], [, b]) => (a.nextService > b.nextService ? 1 : -1));

  if (scheduled.length === 0) return null;

  return (
    <div style={{ ...s.card, marginTop: 0 }}>
      <h3 style={{ margin: '0 0 16px', fontSize: 15 }}>📅 Maintenance Schedule</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {scheduled.map(([name, analysis]) => {
          const cfg  = STATUS_CFG[analysis.status] || STATUS_CFG.UNKNOWN;
          const icon = COMPONENT_ICONS[name] || '⚙️';
          const daysUntil = analysis.rulHours ? Math.round(analysis.rulHours * 0.8 / 24) : null;
          return (
            <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', background: cfg.bg, borderRadius: 6, border: `1px solid ${cfg.border}` }}>
              <span style={{ fontSize: 18 }}>{icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 13 }}>{name.replace(/_/g, ' ')}</div>
                <div style={{ fontSize: 11, color: '#666' }}>Recommended by {analysis.nextService}</div>
              </div>
              {daysUntil != null && (
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontWeight: 700, color: cfg.color, fontSize: 14 }}>
                    {daysUntil <= 0 ? 'NOW' : `${daysUntil}d`}
                  </div>
                  <div style={{ fontSize: 10, color: '#888' }}>until service</div>
                </div>
              )}
              <span style={{ ...s.statusBadge, background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}` }}>
                {analysis.status}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function HowItWorks() {
  return (
    <div style={{ background: '#f0f4f8', borderRadius: 8, padding: 20, marginTop: 8 }}>
      <h3 style={{ margin: '0 0 12px', fontSize: 15 }}>How Predictive Maintenance Works</h3>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
        {[
          ['📉 RUL Estimation', 'Linear regression on 24h of SiteWise data extrapolates when ClampForce or CycleTime will reach failure threshold'],
          ['📊 Wear Detection', 'Rolling variance analysis — increasing variance in ClampForce signals mechanical wear or contamination'],
          ['📈 Trend Analysis', 'Detects consistent degradation trends across CycleTime, OEE and ClampForce over time'],
          ['🗓️ Scheduling', 'Service recommended at 80% of estimated RUL — balances uptime with preventive maintenance'],
        ].map(([title, desc]) => (
          <div key={title} style={{ background: '#fff', borderRadius: 6, padding: 12 }}>
            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{title}</div>
            <div style={{ fontSize: 12, color: '#666', lineHeight: 1.5 }}>{desc}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

const s = {
  page:        { maxWidth: 1100, margin: '0 auto', fontFamily: 'sans-serif' },
  header:      { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 12 },
  heading:     { fontSize: 22, fontWeight: 700, marginBottom: 4 },
  sub:         { color: '#666', fontSize: 13, margin: 0 },
  refreshBtn:  { padding: '8px 16px', background: '#0073bb', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 },
  errorBox:    { background: '#fdecea', color: '#c0392b', padding: '10px 16px', borderRadius: 6, marginBottom: 12, fontSize: 13 },
  lastRun:     { fontSize: 11, color: '#aaa', marginBottom: 16 },
  cardGrid:    { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16, marginBottom: 16 },
  card:        { background: '#fff', borderRadius: 8, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.1)' },
  statusBadge: { padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' },
  barBg:       { background: '#f0f0f0', borderRadius: 4, height: 8, overflow: 'hidden' },
  barFill:     { height: '100%', borderRadius: 4, transition: 'width 0.5s ease' },
  infoRow:     { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 8px', borderRadius: 4, marginBottom: 4 },
};
